import { useState, useRef, useCallback, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack, useRouter, useLocalSearchParams, type Href } from "expo-router";
import { useForm } from "react-hook-form";
import { ScrollView, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { BackButton } from "@/shared/components/ui/back-button";
import { ScreenContainer } from "@/shared/components/ui/screen-container";

import { supabase } from "@lezz/supabase";
import {
  useUpdateProfile,
  useUsernameAvailability,
} from "@/features/profile/api/profile";
import { posthog } from "@/shared/lib/posthog";
import { AvatarPicker } from "@/features/profile/components/avatar-picker";
import { Button } from "@/shared/components/ui/button";
import { FormInput } from "@/shared/components/ui/form-input";
import { FormSelect } from "@/shared/components/ui/form-select";
import { Toggle } from "@/shared/components/ui/toggle";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import { unregisterPushToken } from "@/features/notifications/lib/unregister";
import {
  ProfileNameFormData,
  profileNameSchema,
  ProfileUsernameFormData,
  profileUsernameSchema,
  ProfileLocationFormData,
  profileLocationSchema,
  ProfileInstagramFormData,
  profileInstagramSchema,
} from "@/features/profile/validation/schema";
import { useStates } from "@/features/location/api/states";
import { useCitiesByState } from "@/features/location/api/cities";
import { DEFAULT_COUNTRY } from "@lezz/shared/constants";
import { logger } from "@/shared/lib/logger";

type WizardData = {
  displayName: string;
  username: string;
  state: string;
  city: string;
  instagramUsername: string;
  shouldShowInstagram: boolean;
};

function NameStep({ onNext }: { onNext: (name: string) => void }) {
  const { control, handleSubmit } = useForm<ProfileNameFormData>({
    resolver: zodResolver(profileNameSchema),
    defaultValues: { displayName: "" },
  });

  return (
    <>
      <Text className="text-foreground font-display mb-6 text-2xl font-semibold">
        Qual o seu nome?
      </Text>

      <FormInput
        control={control}
        name="displayName"
        label="Nome"
        placeholder="Seu nome"
        autoCapitalize="words"
      />

      <Button
        size="lg"
        className="mt-4"
        onPress={handleSubmit((data) => onNext(data.displayName!))}
      >
        Continuar
      </Button>
    </>
  );
}

function UsernameStep({ onNext }: { onNext: (username: string) => void }) {
  const primary = useCSSVariable("--color-primary") as string;
  const { control, handleSubmit, watch, setError } =
    useForm<ProfileUsernameFormData>({
      resolver: zodResolver(profileUsernameSchema),
      defaultValues: { username: "" },
    });

  const usernameValue = watch("username") ?? "";
  const [debouncedUsername, setDebouncedUsername] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUsername(usernameValue), 500);
    return () => clearTimeout(timer);
  }, [usernameValue]);

  const isValidFormat = profileUsernameSchema.safeParse({
    username: debouncedUsername,
  }).success;

  const { data: availabilityData, isFetching } = useUsernameAvailability(
    debouncedUsername,
    isValidFormat
  );

  const isTaken = availabilityData?.available === false;
  const isAvailable = availabilityData?.available === true;

  return (
    <>
      <Text className="text-foreground font-display mb-6 text-2xl font-semibold">
        Escolha seu @
      </Text>

      <FormInput
        control={control}
        name="username"
        label="Nome na plataforma"
        placeholder="username"
        prefix="@"
        autoComplete="username"
      />

      {isValidFormat && isFetching && (
        <Text className="text-muted-foreground -mt-2 mb-4 px-4 text-xs">
          Verificando disponibilidade...
        </Text>
      )}
      {!isFetching && isAvailable && (
        <View className="-mt-2 mb-4 flex-row items-center gap-1 px-4">
          <Text className="text-xs text-green-500">
            Nome de usuário disponível
          </Text>
          <Ionicons name="checkmark" size={14} color={primary} />
        </View>
      )}
      {!isFetching && isTaken && (
        <Text className="text-destructive -mt-2 mb-4 px-4 text-xs">
          Esse nome de usuário já está em uso
        </Text>
      )}

      <Button
        size="lg"
        className="mt-4"
        disabled={isTaken || (isValidFormat && isFetching)}
        onPress={handleSubmit((data) => {
          if (isTaken) {
            setError("username", { message: "Esse nome já está em uso" });
            return;
          }
          onNext(data.username);
        })}
      >
        Continuar
      </Button>
    </>
  );
}

function LocationStep({
  onNext,
}: {
  onNext: (state: string, city: string) => void;
}) {
  const { control, handleSubmit, watch, setValue } =
    useForm<ProfileLocationFormData>({
      resolver: zodResolver(profileLocationSchema),
      defaultValues: { state: "", city: "" },
    });

  const { data: states = [], isLoading: isLoadingStates } = useStates();
  const selectedState = watch("state");
  const { data: cities = [], isLoading: isLoadingCities } =
    useCitiesByState(selectedState);

  useEffect(() => {
    if (selectedState) {
      setValue("city", "");
    }
  }, [selectedState, setValue]);

  const stateOptions = states.map((s) => ({ label: s, value: s }));
  const cityOptions = cities.map((c) => ({
    label: c.cityName,
    value: c.cityName,
  }));

  return (
    <>
      <Text className="text-foreground font-display mb-6 text-2xl font-semibold">
        Onde você mora?
      </Text>

      <FormSelect
        control={control}
        name="state"
        label="Estado"
        placeholder="Selecione o estado"
        options={stateOptions}
        isLoading={isLoadingStates}
      />

      <FormSelect
        control={control}
        name="city"
        label="Cidade"
        placeholder="Selecione a cidade"
        options={cityOptions}
        isLoading={isLoadingCities}
        disabled={!selectedState}
        searchable
        searchPlaceholder="Buscar cidade..."
      />

      <Button
        size="lg"
        className="mt-4"
        onPress={handleSubmit((data) => onNext(data.state!, data.city!))}
      >
        Continuar
      </Button>
    </>
  );
}

function InstagramStep({
  onNext,
  onSkip,
}: {
  onNext: (instagramUsername: string, shouldShowInstagram: boolean) => void;
  onSkip: () => void;
}) {
  const { control, handleSubmit } = useForm<ProfileInstagramFormData>({
    resolver: zodResolver(profileInstagramSchema),
    defaultValues: { instagramUsername: "" },
  });
  const [shouldShowInstagram, setShouldShowInstagram] = useState(true);

  return (
    <>
      <Text className="text-foreground font-display mb-6 text-2xl font-semibold">
        Qual seu Instagram?
      </Text>

      <FormInput
        control={control}
        name="instagramUsername"
        label="Instagram"
        placeholder="seuinstagram"
        prefix="@"
        autoCapitalize="none"
      />

      <Toggle
        value={shouldShowInstagram}
        onValueChange={setShouldShowInstagram}
        label="Exibir Instagram no perfil"
        description="Outras usuárias poderão ver seu @"
      />

      <Button
        size="lg"
        className="mt-4"
        onPress={handleSubmit((data) =>
          onNext(data.instagramUsername ?? "", shouldShowInstagram)
        )}
      >
        Continuar
      </Button>

      <Button size="lg" variant="ghost" className="mt-2" onPress={onSkip}>
        Pular
      </Button>
    </>
  );
}

function AvatarStep({
  onSubmit,
  onSkip,
  isLoading,
  initials,
  error,
}: {
  onSubmit: (avatarUri: string) => void;
  onSkip: () => void;
  isLoading: boolean;
  initials?: string;
  error?: string | null;
}) {
  const [avatarUri, setAvatarUri] = useState<string | null>(null);

  return (
    <>
      <Text className="text-foreground font-display mb-6 text-2xl font-semibold">
        Escolha sua foto de perfil
      </Text>

      <AvatarPicker
        avatarUri={avatarUri}
        onChangeAvatar={setAvatarUri}
        initials={initials}
      />

      {error && <Text className="text-destructive mb-2 text-sm">{error}</Text>}

      {avatarUri ? (
        <Button
          size="lg"
          className="mt-2"
          onPress={() => onSubmit(avatarUri)}
          loading={isLoading}
        >
          Salvar foto
        </Button>
      ) : (
        <Button size="lg" className="mt-2" onPress={onSkip} loading={isLoading}>
          Continuar
        </Button>
      )}
    </>
  );
}

export default function CompleteProfileScreen() {
  const { session } = useAuthContext();
  const router = useRouter();
  const { redirect } = useLocalSearchParams<{ redirect?: string }>();
  const updateProfile = useUpdateProfile();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const wizardData = useRef<Partial<WizardData>>({});

  useEffect(() => {
    posthog?.capture("profile_setup_started");
  }, []);

  const handleBack = useCallback(async () => {
    if (currentStep === 1) {
      await unregisterPushToken();
      await supabase.auth.signOut();
      router.replace("/");
    } else {
      setCurrentStep((s) => (s - 1) as 1 | 2 | 3 | 4 | 5);
    }
  }, [currentStep, router]);

  const submitProfile = useCallback(
    (avatarUrl: string | null) => {
      if (!session?.user.id) {
        setSubmitError("Você precisa estar logada para completar o perfil.");
        return;
      }

      setSubmitError(null);
      updateProfile.mutate(
        {
          userId: session.user.id,
          data: {
            displayName: wizardData.current.displayName!,
            username: wizardData.current.username!,
            state: wizardData.current.state!,
            city: wizardData.current.city!,
            country: DEFAULT_COUNTRY,
            instagramUsername: wizardData.current.instagramUsername ?? "",
            shouldShowInstagram:
              wizardData.current.shouldShowInstagram ?? false,
            avatarUrl,
          },
        },
        {
          onSuccess: () => {
            posthog?.capture("profile_completed", {
              has_instagram: !!wizardData.current.instagramUsername,
              has_avatar: !!avatarUrl,
              city: wizardData.current.city ?? null,
              state: wizardData.current.state ?? null,
            });
            router.replace((redirect ?? "/") as Href);
          },
          onError: (error) => {
            if (error.message === "username_taken") {
              setSubmitError(
                "Esse nome de usuário já está em uso. Escolha outro."
              );
              setCurrentStep(2);
            } else {
              logger.error(error.message, error);
              setSubmitError(
                "Não foi possível salvar o perfil. Tente novamente."
              );
            }
          },
        }
      );
    },
    [session, updateProfile, router, redirect]
  );

  return (
    <>
      <Stack.Screen
        options={{ title: "Complete Profile", headerShown: false }}
      />
      <ScreenContainer style={{ paddingTop: 0 }}>
        <ScrollView
          className="flex-1"
          contentContainerClassName="grow"
          keyboardShouldPersistTaps="handled"
        >
          <View className="p-6">
            <BackButton
              className="mt-8 mb-8"
              onPress={handleBack}
              accessibilityLabel="Voltar"
            />

            <Text className="text-muted-foreground mb-2 text-sm">
              Vamos completar seu cadastro
            </Text>

            {currentStep === 1 && (
              <NameStep
                onNext={(name) => {
                  wizardData.current.displayName = name;
                  posthog?.capture("profile_setup_name_completed");
                  setCurrentStep(2);
                }}
              />
            )}
            {currentStep === 2 && (
              <UsernameStep
                onNext={(username) => {
                  wizardData.current.username = username;
                  posthog?.capture("profile_setup_username_completed");
                  setCurrentStep(3);
                }}
              />
            )}
            {currentStep === 3 && (
              <LocationStep
                onNext={(state, city) => {
                  wizardData.current.state = state;
                  wizardData.current.city = city;
                  posthog?.capture("profile_setup_location_completed");
                  setCurrentStep(4);
                }}
              />
            )}
            {currentStep === 4 && (
              <InstagramStep
                onNext={(instagramUsername, shouldShowInstagram) => {
                  wizardData.current.instagramUsername = instagramUsername;
                  wizardData.current.shouldShowInstagram = shouldShowInstagram;
                  posthog?.capture("profile_setup_instagram_completed", {
                    skipped: false,
                  });
                  setCurrentStep(5);
                }}
                onSkip={() => {
                  wizardData.current.instagramUsername = "";
                  wizardData.current.shouldShowInstagram = true;
                  posthog?.capture("profile_setup_instagram_completed", {
                    skipped: true,
                  });
                  setCurrentStep(5);
                }}
              />
            )}
            {currentStep === 5 && (
              <AvatarStep
                onSubmit={(avatarUri) => submitProfile(avatarUri)}
                onSkip={() => submitProfile(null)}
                isLoading={updateProfile.isPending}
                initials={
                  wizardData.current.displayName?.[0]?.toUpperCase() ??
                  wizardData.current.username?.[0]?.toUpperCase() ??
                  "?"
                }
                error={submitError}
              />
            )}
          </View>
        </ScrollView>
      </ScreenContainer>
    </>
  );
}
